import type { CoteraDuckDB } from '../db';
import { duckDbIdentifier, duckDbStringLiteral } from './sql';
import { DuckDbTable } from './table';
import type { DuckDbColumn, DuckDbLayerStack } from './layers';

/**
 * Everything a parquet-backed table needs from the database, and no more, so a
 * spec can hand it an object literal of three `vi.fn()`s without a cast.
 */
export type ParquetBackedTableDb = Pick<
  CoteraDuckDB,
  'rawQuery' | 'replaceFile' | 'registerFileURL' | 'replaceFileURL'
>;

/**
 * A DuckDB table whose contents are a parquet snapshot with mutations replayed
 * on top.
 *
 * The base is downloaded infrequently and cached; the head is whatever has
 * happened since — an operation log, unsaved local edits, live events — carried
 * by the layer stack and replayed on every rebuild. Knowing nothing about what
 * the rows mean is the point: how a table is keyed, and whether anything joins
 * it, are properties of the caller.
 *
 * The rebuild and the replay are one `write`, so they are one unit as far as
 * any other writer to this table is concerned. Splitting them is what used to
 * let a second run's `CREATE OR REPLACE` land in the middle of the first one's
 * replay and surface as "Column with name X already exists".
 */
export class ParquetBackedTable {
  /** The quoted identifier, for whatever reads or joins this. */
  readonly sql: string;

  private readonly table: DuckDbTable;

  constructor(
    readonly name: string,
    private readonly db: Promise<ParquetBackedTableDb>
  ) {
    this.sql = duckDbIdentifier(name);
    // No declared columns: the schema is whatever the parquet has, so the
    // table is created by the query that fills it.
    this.table = new DuckDbTable({ name });
  }

  /**
   * Points DuckDB at a parquet over HTTP.
   *
   * Idempotent, and cached by name inside `CoteraDuckDB` — so a name has to
   * identify its contents, which in practice means an immutable artifact id.
   *
   * `replace` overrides that cache for the one case it is wrong: a re-read after
   * the last URL for this name stopped answering — an expired signature, or an
   * artifact pruned under an actively-written dataset. Without it a fresh URL
   * resolved upstream would be dropped in favour of the dead one.
   */
  async register(
    fileName: string,
    url: string,
    options: { replace?: boolean } = {}
  ): Promise<void> {
    const db = await this.db;
    if (options.replace === true) {
      await db.replaceFileURL(fileName, url);
      return;
    }
    await db.registerFileURL(fileName, url);
  }

  /**
   * Rebuilds from the parquet, then replays the stack's mutating layers.
   *
   * Returns the schema the table ends up with — which comes out of the same
   * fold that produced the statements, so a column a layer adds cannot be
   * missing from it and one a layer drops cannot linger. Returns `undefined`
   * when a later run superseded this one.
   */
  async load<TRow>(
    params: {
      /** As registered by {@link register}. */
      fileName: string;
      /**
       * Extra projection over the parquet, e.g. a synthetic ordering column.
       * Appended to `SELECT *`, so it must start with a comma.
       */
      select?: string;
      layers: DuckDbLayerStack<TRow>;
      /** The parquet's own schema, before any layer has run. */
      baseColumns?: readonly DuckDbColumn[];
      resolveRowIdColumn?: (columns: readonly DuckDbColumn[]) => string | null;
    },
    options: { shouldRun?: () => boolean } = {}
  ): Promise<readonly DuckDbColumn[] | undefined> {
    const db = await this.db;
    return await this.table.write(
      db,
      async (writer) => {
        // Derived from the query, so the parquet's own schema is the table's,
        // and there is no window in which the table is missing.
        await writer.replace({
          from: 'query',
          sql: `SELECT *${
            params.select ?? ''
          } FROM read_parquet(${duckDbStringLiteral(params.fileName)})`,
        });
        return await params.layers.materialize({
          exec: writer.exec,
          tableName: this.name,
          baseColumns: params.baseColumns ?? [],
          ...(params.resolveRowIdColumn !== undefined && {
            resolveRowIdColumn: params.resolveRowIdColumn,
          }),
        });
      },
      options
    );
  }

  /**
   * Replays a stack onto the table as it already stands, without rebuilding it.
   *
   * The counterpart to {@link load} for a change small enough to apply directly:
   * same table, same lock, same fold, but no download and no `CREATE OR
   * REPLACE`. What it buys is everything downstream — the grid's rows, its
   * scroll position, its selection — surviving a change that {@link load} would
   * have thrown them away for.
   *
   * Deliberately without `shouldRun`. `load` may skip a superseded run because a
   * later one rebuilds from the parquet and subsumes it; an increment is the
   * only thing that will ever apply itself, so dropping it loses the write.
   *
   * Serialized against `load` by the shared lock, which is what stops an
   * increment landing in the middle of a rebuild's replay. It does *not* stop an
   * increment being applied to a rebuild that already included it — only the
   * caller knows what it has replayed, so ordering that is the caller's job.
   */
  async apply<TRow>(params: {
    layers: DuckDbLayerStack<TRow>;
    /** The schema the table already has, which the fold starts from. */
    baseColumns: readonly DuckDbColumn[];
    resolveRowIdColumn?: (columns: readonly DuckDbColumn[]) => string | null;
  }): Promise<void> {
    const db = await this.db;
    await this.table.write(db, async (writer) => {
      await params.layers.materialize({
        exec: writer.exec,
        tableName: this.name,
        baseColumns: params.baseColumns,
        ...(params.resolveRowIdColumn !== undefined && {
          resolveRowIdColumn: params.resolveRowIdColumn,
        }),
      });
    });
  }
}
